import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";

interface UseDevAgentChromeInput {
  orgId?: string;
  userId?: string;
  setGlobalSidebarOpen?: ((open: boolean) => void) | undefined;
}

export function useDevAgentChrome({
  orgId,
  userId,
  setGlobalSidebarOpen,
}: UseDevAgentChromeInput) {
  const [reauthModalOpen, setReauthModalOpen] = useState(false);
  const previousOrgRef = useRef(orgId);
  const previousUserRef = useRef(userId);

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (event.message?.includes("TextModel got disposed")) {
        event.preventDefault();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  useEffect(() => {
    import("../errorHandler").then(({ setReauthHandler }) => {
      setReauthHandler(() => setReauthModalOpen(true));
    });
  }, []);

  // Keep a ref to the latest setter so the mount-only effect never goes stale
  // while also never re-running when the sidebar state changes (which would
  // recreate setOpen and fight the user's toggle click).
  const setGlobalSidebarOpenRef = useRef(setGlobalSidebarOpen);
  useLayoutEffect(() => {
    setGlobalSidebarOpenRef.current = setGlobalSidebarOpen;
  });

  useEffect(() => {
    setGlobalSidebarOpenRef.current?.(false);
    return () => {
      setGlobalSidebarOpenRef.current?.(true);
    };
  }, []); // intentionally mount/unmount only

  useEffect(() => {
    const orgChanged = previousOrgRef.current && orgId !== previousOrgRef.current;
    const userChanged =
      previousUserRef.current && userId !== previousUserRef.current;

    if (orgChanged || userChanged) {
      if (orgChanged) {
        import("../devAgentService").then(({ resetOrgContext }) => {
          resetOrgContext({
            old_org_id: previousOrgRef.current,
            new_org_id: orgId,
          });
        });
      }
      useDevAgentStore.getState().reset();
    }

    previousOrgRef.current = orgId;
    previousUserRef.current = userId;
  }, [orgId, userId]);

  return {
    reauthModalOpen,
    setReauthModalOpen,
  };
}
